import React, { memo, useCallback, useState, useMemo, useEffect } from 'react';
import PageHeader from '../../../../components/PageHeader/PageHeader';
import { Box, Button, FormHelperText, TextField, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Tooltip, Stack, Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import AutocompleteStatic from './AutocompleteStatic';
import VehiclesInfo from './VehiclesInfo';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddBoxIcon from '@mui/icons-material/AddBox';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
// Client / exporter / consignee moved to shipment root; related components removed from container level.

const CONTAINER_SIZES = ['20ft', '40ft', '40ft HC', '45ft HC'];

// Reusable text field wired to nested Formik path (e.g. containers[0].sealNumber)
const FormikTextField = memo(function FormikTextField({ name, label, disabled, formik }) {
  const getByPath = (obj, path) => {
    if (!obj || !path) return undefined;
    const norm = path.replace(/\[(\w+)\]/g, ".$1").replace(/\['([^']+)'\]/g, ".$1");
    return norm.split('.').filter(Boolean).reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
  };
  const value = getByPath(formik.values, name) ?? '';
  const touched = getByPath(formik.touched, name);
  const errorMsg = getByPath(formik.errors, name);
  const error = Boolean(touched && errorMsg);


 

  


  return (
    <Box display="flex" flexDirection="column">
      <TextField
        label={label}
        variant="outlined"
        disabled={disabled}
        name={name}
        value={value}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={error}
      />
      {error && <FormHelperText error>{errorMsg}</FormHelperText>}
    </Box>
  );
});

function ContainerInfo({ disabled }) {
  const { t } = useTranslation();
  const formik = useFormikContext();
  const { values, setFieldValue } = formik;
  const containers = useMemo(() => values?.containers || [], [values?.containers]);

  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  // Ensure each container has a stable local id so React keys don't shift and "move" vehicles visually
  useEffect(() => {
    const needsIds = containers.some(c => !c || !c._localId);
    if (needsIds) {
      const withIds = containers.map(c => c? { ...c, _localId: c._localId || `${Date.now()}_${Math.random().toString(36).slice(2,8)}` } : c);
      setFieldValue('containers', withIds, false);
    }
  }, [containers, setFieldValue]);

  // Re-add container with full nested structure
  const handleAdd = useCallback(() => {
    const newContainer = {
      _localId: `${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
      containerSize: null,
      containerNumber: null,
      sealNumber: null,
      netWeight: 0,
      shipmentItems: [],
    };
    setFieldValue('containers', [...containers, newContainer], false);
  }, [containers, setFieldValue]);

  const confirmDelete = useCallback(() => {
    if (pendingDeleteId == null) return;
    const next = containers.filter(c => c && (c._localId || '') !== pendingDeleteId);
    setFieldValue('containers', next, false);
    setPendingDeleteId(null);
  }, [pendingDeleteId, containers, setFieldValue]);



  

  return (
    <Box sx={{ mt: 6 }}>
      <PageHeader title={t('Container Information')} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddBoxIcon />}
          onClick={handleAdd}
          disabled={disabled}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
        >
          {t('Add Container')}
        </Button>
      </Box>

      {containers.length === 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
            borderStyle: 'dashed',
            borderColor: '#b5c4d1',
            bgcolor: '#f5f9fc',
            color: 'text.secondary',
          }}
        >
          {t('No containers added yet')}
        </Paper>
      )}

      <Stack spacing={4} mt={containers.length ? 2 : 0}>
  {containers.map((c, index) => {
          const base = `containers[${index}]`;
          return (
            <Paper
              key={c?._localId || index}
              elevation={0}
              sx={{
                position: 'relative',
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: '1px solid #e1e8ef',
                bgcolor: '#f8f8f8ff',
                boxShadow: '0 2px 8px rgba(11,60,93,0.06)',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                <LocalShippingIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1, color: '#0B3C5D' }}>
                  {t('Container')} #{index + 1}
                </Typography>
                <Tooltip title={t('Delete')}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => setPendingDeleteId(c?._localId || `${index}`)}
                      disabled={disabled}
                      sx={{ color:"white",bgcolor: 'error.light', '&:hover': { bgcolor: 'error.main', color: '#fff' } }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
                  gap: { xs: 2, md: 3 },
                  mb: 2,
                }}
              >
                <FormikTextField name={`${base}.containerNumber`} label={t('Container Number')} disabled={disabled} formik={formik} />
                <FormikTextField name={`${base}.sealNumber`} label={t('Seal Number')} disabled={disabled} formik={formik} />

                <AutocompleteStatic
                  disabled={disabled}
                  formik={formik}
                  options={CONTAINER_SIZES}
                  label={t('Container Size')}
                  fieldName={`${base}.containerSize`}
                  errorKey={`${base}.containerSize`}
                />
              </Box>

              <Divider sx={{ my: 3 }} />
              <VehiclesInfo
                disabled={disabled}
                basePath={base}
                compact
                index={index}
                title={t('Vehicles in Container')}
              />


              <Divider sx={{ my: 3 }} />
              {/* Exporter / Consignee info moved to shipment-level component */}


            </Paper>
          );
        })}
      </Stack>

    <Dialog open={pendingDeleteId != null} onClose={() => setPendingDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('Delete Container')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('Are you sure you want to delete this container? This action cannot be undone.')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
      <Button onClick={() => setPendingDeleteId(null)}>{t('Cancel')}</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} autoFocus>
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default memo(ContainerInfo);